import { businessInitials, businessMarkColor, type BusinessLogo } from "@teamna/shared";

/**
 * The mark of a business: its logo, or its initials on a colour when it has no logo.
 *
 * Every place that names a business draws this one component (the sidebar, the top bar, the member
 * home, the business chips, the Brand preview), so an upload changes all of them at once. The
 * Teamna drops are not a business mark: they stay on the sign-in screen and the app icon.
 *
 * The mark is decoration next to the business name, which is always written beside it, so it is
 * hidden from screen readers.
 *
 * On a dark surface (`data-tone="dark"`, the Control Center sidebar) a logo the owner marked
 * "plate on dark" sits on a small white plate (`.business-mark[data-plate="true"]` in styles.css).
 */
export type MarkBusiness = {
  businessId: string;
  name: string;
  /** Used for the initials when the name has no Latin letter. */
  slug?: string;
  logo?: BusinessLogo | null;
};

export function BusinessMark({
  business,
  size = 36,
  className = "",
}: {
  business: MarkBusiness;
  /** The side of the square, in pixels. */
  size?: number;
  className?: string;
}) {
  const logo = business.logo ?? null;
  const box = { width: size, height: size };
  if (logo) {
    return (
      <span
        aria-hidden="true"
        data-testid="business-mark"
        data-plate={logo.plateOnDark ? "true" : undefined}
        className={`business-mark inline-flex shrink-0 overflow-hidden rounded-[24%] ${className}`}
        style={box}
      >
        <img src={logo.markUrl} alt="" width={size} height={size} className="size-full object-contain" draggable={false} />
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      data-testid="business-mark"
      className={`inline-flex shrink-0 items-center justify-center rounded-[24%] font-bold text-white ${className}`}
      style={{ ...box, backgroundColor: businessMarkColor(business.businessId), fontSize: Math.round(size * 0.38) }}
    >
      {businessInitials(business.name, business.slug)}
    </span>
  );
}
